import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft,
  Loader2,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Bug,
  Lightbulb,
  User,
  Plus,
  ChevronRight,
  Mail,
  Calendar,
  BookOpen,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

// My Tickets Help Modal Component
const MyTicketsHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Support Tickets
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This page shows all your support tickets — both active and resolved. You can track the status of your requests and view responses from our support team.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">
              We want to make sure every question gets answered. This page keeps all your communication with our team in one place, with full history of each conversation.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Click any ticket to see the full conversation',
                'Look for "New Response" badges for unread replies',
                'We typically respond within 24-48 business hours',
                'Check the Help Center first for quick answers'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_CONFIG = {
  OPEN: { label: 'Open', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  RESOLVED: { label: 'Resolved', color: 'bg-lime-100 text-lime-700 border-lime-200', icon: CheckCircle },
  CLOSED: { label: 'Closed', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: CheckCircle },
};

const CATEGORY_CONFIG = {
  general: { label: 'General Question', icon: HelpCircle },
  account: { label: 'Account Issue', icon: User },
  matching: { label: 'Job Matching', icon: AlertCircle },
  bug: { label: 'Bug Report', icon: Bug },
  feedback: { label: 'Feature Request', icon: Lightbulb },
};

// Ticket Card Component
const TicketCard = ({ ticket, onClick, hasUnread }) => {
  const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
  const category = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.general;
  const StatusIcon = status.icon;
  const CategoryIcon = category.icon;

  return (
    <Card 
      className={`hover:shadow-md transition-all cursor-pointer group ${hasUnread ? 'border-lime-400 bg-lime-50/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <CategoryIcon className="h-5 w-5 text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs font-mono">
                    {ticket.ticket_number}
                  </Badge>
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                  {hasUnread && (
                    <Badge className="bg-lime-500 text-white">New Response</Badge>
                  )}
                </div>
                <h4 className="font-medium text-slate-900 group-hover:text-lime-700 transition-colors">
                  {ticket.subject}
                </h4>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-lime-500 transition-colors flex-shrink-0" />
            </div>
            <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.message}</p>
            <div className="flex items-center gap-3 text-xs text-slate-400 mt-2">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(ticket.created_at), 'MMM d, yyyy')}
              </span>
              {ticket.admin_response && (
                <span className="flex items-center gap-1 text-lime-600">
                  <MessageSquare className="h-3 w-3" />
                  Response received
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Ticket Detail Modal
const TicketDetailModal = ({ ticket, isOpen, onClose }) => {
  if (!ticket) return null;

  const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.OPEN;
  const category = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.general;
  const CategoryIcon = category.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="font-mono">{ticket.ticket_number}</Badge>
            <Badge className={status.color}>{status.label}</Badge>
          </div>
          <DialogTitle>{ticket.subject}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Original Message */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <User className="h-4 w-4 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">You</p>
                <p className="text-xs text-slate-500">
                  {format(new Date(ticket.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                </p>
              </div>
            </div>
            <div className="ml-10 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <CategoryIcon className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-500">{category.label}</span>
              </div>
              <p className="text-slate-700 whitespace-pre-wrap">{ticket.message}</p>
            </div>
          </div>

          {/* Admin Response */}
          {ticket.admin_response && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-lime-100 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-lime-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Varyynt Support</p>
                  <p className="text-xs text-slate-500">
                    {ticket.responded_at 
                      ? format(new Date(ticket.responded_at), 'MMM d, yyyy \'at\' h:mm a')
                      : 'Recently'
                    }
                  </p>
                </div>
              </div>
              <div className="ml-10 p-4 bg-lime-50 rounded-lg border border-lime-200">
                <p className="text-slate-700 whitespace-pre-wrap">{ticket.admin_response}</p>
              </div>
            </div>
          )}

          {/* Status Info */}
          {ticket.status === 'RESOLVED' && ticket.resolved_at && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-lime-500" />
                Resolved on {format(new Date(ticket.resolved_at), 'MMM d, yyyy')}
              </p>
            </div>
          )}

          {/* Waiting Message */}
          {!ticket.admin_response && ticket.status === 'OPEN' && (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Awaiting response from support team. We typically respond within 24-48 business hours.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function MyTicketsPage() {
  const { authAxios, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const loadTickets = useCallback(async () => {
    try {
      const response = await authAxios.get('/candidates/support/tickets');
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load your tickets');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleViewTicket = async (ticket) => {
    // Mark as seen when viewing
    if (ticket.admin_response && !ticket.user_has_seen_response) {
      try {
        await authAxios.get(`/candidates/support/tickets/${ticket.id}`);
        // Update local state
        setTickets(prev => prev.map(t => 
          t.id === ticket.id ? { ...t, user_has_seen_response: true } : t
        ));
      } catch (e) {
        // Ignore errors
      }
    }
    setSelectedTicket(ticket);
    setDetailOpen(true);
  };

  const openTickets = tickets.filter(t => t.status === 'OPEN' || t.status === 'IN_PROGRESS');
  const closedTickets = tickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              Sign Out
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-500 hover:text-lime-600"
              onClick={() => setShowHelp(true)}
              data-testid="tickets-help-btn"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading text-slate-900 mb-1">My Support Tickets</h1>
            <p className="text-slate-500">Track your support requests and responses</p>
          </div>
          <Button 
            onClick={() => navigate('/support')}
            className="bg-evergreen-950 hover:bg-evergreen-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
          </div>
        ) : tickets.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No tickets yet</h3>
              <p className="text-slate-500 mb-6">
                Have a question or need help? Submit a support ticket and we'll get back to you.
              </p>
              <Button onClick={() => navigate('/support')} className="bg-evergreen-950 hover:bg-evergreen-900">
                <Mail className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Open Tickets */}
            {openTickets.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Active Tickets ({openTickets.length})
                </h2>
                <div className="space-y-3">
                  {openTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onClick={() => handleViewTicket(ticket)}
                      hasUnread={ticket.admin_response && !ticket.user_has_seen_response}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Resolved Tickets */}
            {closedTickets.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-lime-500" />
                  Resolved ({closedTickets.length})
                </h2>
                <div className="space-y-3">
                  {closedTickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      onClick={() => handleViewTicket(ticket)}
                      hasUnread={ticket.admin_response && !ticket.user_has_seen_response}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Help Link */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">
            Looking for quick answers?{' '}
            <Link to="/help" className="text-lime-600 hover:text-lime-700 font-medium">
              Browse our Help Center
            </Link>
          </p>
        </div>
      </main>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
      />

      {/* My Tickets Help Modal */}
      <MyTicketsHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
